import {
    TOOLS,
    WORKSPACES,
    type ToolConfig,
    type WorkspaceConfig,
} from '../layout/workspace-config';
import { sortToolsByLabel } from '../layout/workspace-tool-order';

/** Category for command palette grouping */
export type CommandCategory = 'workspace' | 'tool' | 'action' | 'personnel';

/** A single command in the palette */
export interface Command {
    id: string;
    label: string;
    icon: string;
    category: CommandCategory;
    keywords: string[];
    /** Called when the command is executed */
    action: () => void;
    /** Optional keyboard shortcut display hint */
    shortcut?: string;
}

/** Personnel record for command palette */
export interface PersonnelRecord {
    id: string;
    name: string;
    role?: string;
}

export interface BuildCommandListOptions {
    workspaces?: WorkspaceConfig[];
    tools?: ToolConfig[];
}

/**
 * Build the full command list from workspace config + dynamic data.
 *
 * @param switchToWorkspace - callback to navigate to a workspace
 * @param switchToTool - callback to navigate to a tool
 * @param personnel - optional list of personnel to include as commands
 * @param onSelectPersonnel - callback when a personnel command is selected
 */
export function buildCommandList(
    switchToWorkspace: (id: string) => void,
    switchToTool: (id: string) => void,
    personnel?: PersonnelRecord[],
    onSelectPersonnel?: (id: string) => void,
    options: BuildCommandListOptions = {},
): Command[] {
    const commands: Command[] = [];
    const workspaces = sortToolsByLabel(options.workspaces ?? WORKSPACES);
    const tools = sortToolsByLabel(options.tools ?? TOOLS);

    // --- Workspaces ---
    for (const ws of workspaces) {
        commands.push({
            id: `ws-${ws.id}`,
            label: ws.label,
            icon: ws.icon,
            category: 'workspace',
            keywords: [ws.id, ws.description, ...ws.primaryTools],
            action: () => switchToWorkspace(ws.id),
        });
    }

    // --- Tools ---
    for (const tool of tools) {
        // Skip dev tools in production
        if (tool.id === 'dev' && !import.meta.env.DEV) continue;

        commands.push({
            id: `tool-${tool.id}`,
            label: tool.label,
            icon: tool.icon,
            category: 'tool',
            keywords: [tool.id, tool.route.slice(1)],
            action: () => switchToTool(tool.id),
        });
    }

    // --- Personnel ---
    if (personnel && onSelectPersonnel) {
        const sortedPersonnel = [...personnel].toSorted((a, b) => a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
        }));

        for (const person of sortedPersonnel) {
            commands.push({
                id: `person-${person.id}`,
                label: person.name,
                icon: 'person',
                category: 'personnel',
                keywords: [person.role || '', person.name.toLowerCase()],
                action: () => onSelectPersonnel(person.id),
            });
        }
    }

    return commands;
}

/**
 * Fuzzy search commands by query.
 * Case-insensitive substring match against label + keywords.
 * Prefix matches score higher than substring matches.
 * Returns top 15 results.
 */
export function fuzzySearch(commands: Command[], query: string): Command[] {
    if (!query.trim()) return commands.slice(0, 15);

    const q = query.toLowerCase().trim();

    type ScoredCommand = { command: Command; score: number };

    const scored: ScoredCommand[] = [];

    for (const command of commands) {
        let score = 0;
        const label = command.label.toLowerCase();

        // Label prefix match (highest priority)
        if (label.startsWith(q)) {
            score = 100;
        }
        // Label contains match
        else if (label.includes(q)) {
            score = 50;
        }
        // Keyword match
        else {
            for (const keyword of command.keywords) {
                const kw = keyword.toLowerCase();
                if (kw.startsWith(q)) {
                    score = Math.max(score, 40);
                } else if (kw.includes(q)) {
                    score = Math.max(score, 20);
                }
            }
        }

        if (score > 0) {
            scored.push({ command, score });
        }
    }

    const sorted = scored.toSorted((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.command.label.localeCompare(b.command.label, undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    });

    return sorted.slice(0, 15).map(s => s.command);
}
